/*
 * The live price socket, as one connection shared by the whole application.
 *
 * WHY A CONTEXT AND NOT A HOOK PER COMPONENT
 *
 * The dashboard shows a watchlist, a positions table and a chart at once, and
 * every one of them wants prices. If each opened its own socket, the server
 * would hold three feeds for one person and Zerodha would see three
 * subscriptions of the same instruments — and Zerodha allows three live
 * connections per API key in total. So there is exactly one socket, opened
 * here, and components declare what they are looking at with `useWatch`.
 *
 * The provider unions those declarations and tells the server the result.
 * A component unmounting removes its tokens from the union, which is what
 * stops the subscription growing all afternoon as somebody moves between
 * pages.
 *
 * WHY PRICES ARE NOT IN REACT QUERY
 *
 * They arrive several times a second and belong to no request. Putting them
 * through a query cache would re-render every subscriber on every tick;
 * keeping them in a ref and publishing a version counter lets a component
 * subscribe to one instrument and re-render only when that one moves.
 *
 * WHAT `stale` MEANS, AND WHY IT IS LOAD-BEARING
 *
 * The server stamps every frame with the age of the feed. When that age
 * exceeds the threshold — the socket dropped, the token expired, the market
 * data subscription lapsed — `stale` goes true and every price in the UI is
 * greyed. A price that stopped updating and still looks current is the worst
 * thing this screen could show, so this is not a nicety.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { streamUrl } from "./client";
import type { FeedState, Quote, QuoteFrame } from "./types";

export interface Live {
  /** The last price for one instrument, or undefined if none has arrived. */
  quote: (token: number) => Quote | undefined;
  /** Which way it last moved, for the flash. Resets after the animation. */
  direction: (token: number) => "up" | "down" | null;
  feed: FeedState;
  stale: boolean;
  ageMs: number;
  connected: boolean;
  /** Bumped on every frame, so a subscriber can re-render on new prices. */
  version: number;
}

const LiveContext = createContext<Live | null>(null);
const WatchContext = createContext<{
  register: (id: string, tokens: number[]) => void;
  release: (id: string) => void;
} | null>(null);

let nextWatcherId = 1;

export function LiveProvider({ children }: { children: ReactNode }) {
  const socket = useRef<WebSocket | null>(null);
  const quotes = useRef<Map<number, Quote>>(new Map());
  const directions = useRef<Map<number, "up" | "down">>(new Map());
  const watchers = useRef<Map<string, number[]>>(new Map());
  const retry = useRef(0);
  const retryTimer = useRef<number | null>(null);
  const closing = useRef(false);

  const [version, setVersion] = useState(0);
  const [feed, setFeed] = useState<FeedState>("idle");
  const [stale, setStale] = useState(true);
  const [ageMs, setAgeMs] = useState(-1);
  const [connected, setConnected] = useState(false);

  /** The union of every mounted component's tokens. */
  const union = useCallback((): number[] => {
    const all = new Set<number>();
    watchers.current.forEach((tokens) => tokens.forEach((t) => all.add(t)));
    return [...all];
  }, []);

  const sendWatch = useCallback(() => {
    const ws = socket.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ watch: union() }));
  }, [union]);

  const connect = useCallback(() => {
    if (closing.current) return;
    if (socket.current && socket.current.readyState <= WebSocket.OPEN) return;

    const ws = new WebSocket(streamUrl());
    socket.current = ws;

    ws.onopen = () => {
      retry.current = 0;
      setConnected(true);
      sendWatch();
    };

    ws.onmessage = (event) => {
      let payload: QuoteFrame | { type: string; feed?: FeedState; note?: string };
      try {
        payload = JSON.parse(event.data as string);
      } catch {
        return;
      }

      if (payload.type === "hello") {
        setFeed((payload.feed as FeedState) ?? "idle");
        return;
      }
      if (payload.type !== "quotes") return;

      const frame = payload as QuoteFrame;
      for (const q of frame.quotes) {
        const previous = quotes.current.get(q.token);
        if (previous && q.last !== previous.last) {
          directions.current.set(q.token, q.last > previous.last ? "up" : "down");
        }
        quotes.current.set(q.token, q);
      }
      setFeed(frame.feed);
      setStale(frame.stale);
      setAgeMs(frame.ageMs);
      setVersion((v) => v + 1);
    };

    ws.onclose = () => {
      setConnected(false);
      socket.current = null;
      if (closing.current) return;
      // Back off, but stay responsive: a drop during market hours must be
      // recovered in seconds, not minutes.
      const wait = Math.min(1000 * 2 ** retry.current, 15_000);
      retry.current += 1;
      retryTimer.current = window.setTimeout(connect, wait);
    };

    ws.onerror = () => {
      // `onclose` always follows, and handles the reconnect. Closing here as
      // well would race it.
    };
  }, [sendWatch]);

  useEffect(() => {
    closing.current = false;
    connect();
    return () => {
      closing.current = true;
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
      socket.current?.close();
      socket.current = null;
    };
  }, [connect]);

  /* A backgrounded tab has its timers throttled and its socket sometimes
     dropped without an event. Coming back to the foreground is the moment to
     find out, rather than showing an hour-old price as current. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const ws = socket.current;
      if (!ws || ws.readyState > WebSocket.OPEN) {
        connect();
      } else if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ ping: 1 }));
        sendWatch();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [connect, sendWatch]);

  /* The server marks a frame stale, but only when it sends one. A feed that
     has stopped sending entirely would otherwise stay green for ever, so the
     age is also advanced here. */
  useEffect(() => {
    const timer = window.setInterval(() => {
      setAgeMs((previous) => {
        if (previous < 0) return previous;
        const next = previous + 5000;
        if (next > 20_000) setStale(true);
        return next;
      });
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const register = useCallback(
    (id: string, tokens: number[]) => {
      const existing = watchers.current.get(id);
      // Registering identical tokens happens on every render of every table;
      // comparing first is what keeps that from being a socket message each time.
      if (existing && existing.length === tokens.length && existing.every((t, i) => t === tokens[i])) {
        return;
      }
      watchers.current.set(id, tokens);
      sendWatch();
    },
    [sendWatch],
  );

  const release = useCallback(
    (id: string) => {
      watchers.current.delete(id);
      sendWatch();
    },
    [sendWatch],
  );

  const live = useMemo<Live>(
    () => ({
      quote: (token: number) => quotes.current.get(token),
      direction: (token: number) => directions.current.get(token) ?? null,
      feed,
      stale,
      ageMs,
      connected,
      version,
    }),
    [feed, stale, ageMs, connected, version],
  );

  const watch = useMemo(() => ({ register, release }), [register, release]);

  return (
    <LiveContext.Provider value={live}>
      <WatchContext.Provider value={watch}>{children}</WatchContext.Provider>
    </LiveContext.Provider>
  );
}

export function useLive(): Live {
  const value = useContext(LiveContext);
  if (!value) throw new Error("useLive must be used inside <LiveProvider>");
  return value;
}

/**
 * Declare what this component is looking at.
 *
 * The tokens are joined into a key rather than depended on as an array, so a
 * caller may build the list inline — `useWatch(rows.map(r => r.token))` — with
 * no memo of its own and no re-subscription on every render.
 */
export function useWatch(tokens: number[]): void {
  const watch = useContext(WatchContext);
  const id = useRef<string>("");
  if (!id.current) id.current = `w${nextWatcherId++}`;

  const key = tokens.join(",");

  useEffect(() => {
    if (!watch) return;
    const mine = key ? key.split(",").map(Number) : [];
    watch.register(id.current, mine);
  }, [key, watch]);

  useEffect(() => {
    const mine = id.current;
    return () => watch?.release(mine);
  }, [watch]);
}
